import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { PWAInstallButton } from "@/components/PWAInstallButton";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { Smartphone, Wifi, Zap, Shield, Download, Share, PlusSquare } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const Install = () => {
  const { isInstalled, isInstallable } = usePWAInstall();

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Smartphone className="w-10 h-10 text-primary-foreground" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Install Gamify App
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Get quick access to your gamification strategies anytime. Install our app for a faster, 
              offline-capable experience.
            </p>
            
            <PWAInstallButton 
              variant="default" 
              size="lg" 
              className="bg-gradient-to-r from-primary to-accent hover:opacity-90"
              showOnlyWhenInstallable={false}
            />
          </div>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="border-border/50">
              <CardContent className="pt-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Lightning Fast</h3>
                <p className="text-sm text-muted-foreground">
                  Instant loading with cached resources for a native app feel
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardContent className="pt-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Wifi className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Works Offline</h3>
                <p className="text-sm text-muted-foreground">
                  Access your strategies even without an internet connection
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardContent className="pt-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Secure & Private</h3>
                <p className="text-sm text-muted-foreground">
                  Your data stays on your device with secure local storage
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Manual Installation Instructions */}
          {!isInstallable && !isInstalled && (
            <div className="bg-muted/50 rounded-xl p-8">
              <h2 className="text-2xl font-bold mb-6 text-center">Manual Installation</h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* iOS Instructions */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm">🍎</span>
                    iPhone / iPad (Safari)
                  </h3>
                  <ol className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium shrink-0">1</span>
                      <span>Tap the <Share className="w-4 h-4 inline mx-1" /> Share button at the bottom of the screen</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium shrink-0">2</span>
                      <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium shrink-0">3</span>
                      <span>Tap <strong>"Add"</strong> in the top right corner</span>
                    </li>
                  </ol>
                </div>

                {/* Android Instructions */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm">🤖</span>
                    Android (Chrome)
                  </h3>
                  <ol className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium shrink-0">1</span>
                      <span>Tap the <strong>⋮</strong> menu button in the top right</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium shrink-0">2</span>
                      <span>Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium shrink-0">3</span>
                      <span>Tap <strong>"Install"</strong> to confirm</span>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          {isInstalled && (
            <div className="text-center p-8 bg-primary/5 rounded-xl">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Download className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold mb-2">App Installed!</h2>
              <p className="text-muted-foreground">
                You can now access Gamify from your home screen for quick access.
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Install;
